import Head from 'next/head';

import dayjs from "dayjs";
import "dayjs/locale/ro";
import { cardImageUrl } from "../../utils/cardImageUrl";
import { singleCardUrl } from "../../utils/slugify";
import { getAvailability } from "../../utils/product";
dayjs.locale('ro');

// Map our internal stock state onto schema.org ItemAvailability URLs.
// Google flags Offer markup that omits `availability`, and the value
// drives the in-stock badge in shopping-style rich results.
const availabilitySchema = (product) => {
    switch (getAvailability(product)) {
        case 'available': return 'https://schema.org/InStock';
        case 'preorder': return 'https://schema.org/PreOrder';
        case 'supplier': return 'https://schema.org/BackOrder';
        default: return 'https://schema.org/OutOfStock';
    }
}

// Official profiles for entity grounding (`sameAs`). Dead links hurt, so
// this list is confirmed-only — extend it as new channels go live.
const SOCIAL_PROFILES = [
    "https://www.facebook.com/KritStuff/",
    "https://www.instagram.com/kritstuff",
    "https://discord.gg/kritlandia",
    "https://www.google.com/maps/place/Krit/@44.9355967,26.036745,17z"
];

const stripTags = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
const toIso = (value) => { const d = new Date(value); return isNaN(d.getTime()) ? null : d.toISOString(); };

// Per-store hours, mirrored from the footer contact blocks
// (components/blocks/Contact/*) — keep them in lockstep so the rich
// result never contradicts the on-page hours.
const hours = (days, opens, closes) => days.map((dayOfWeek) => ({
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": `https://schema.org/${dayOfWeek}`,
    opens,
    closes
}));

const PLOIESTI_HOURS = [
    ...hours(["Monday", "Tuesday", "Wednesday"], "11:00", "19:00"),
    ...hours(["Thursday", "Friday", "Saturday"], "11:00", "22:00"),
    ...hours(["Sunday"], "12:00", "20:00")
];
const BRASOV_HOURS = [...hours(["Wednesday", "Thursday", "Friday", "Saturday", "Sunday"], "14:00", "22:00")
, ...hours(["Monday", "Tuesday"], "16:00", "22:00")];

const STORE_ADDRESSES = {
    ploiesti: {
        "@type": "PostalAddress",
        "streetAddress": "Str. Buna Vestire, Nr. 55",
        "addressLocality": "Ploiești",
        "addressRegion": "PH",
        "postalCode": "100551",
        "addressCountry": "RO"
    },
    brasov: {
        "@type": "PostalAddress",
        "streetAddress": "Strada Lungă, Nr. 51",
        "addressLocality": "Brașov",
        "addressRegion": "BV",
        "addressCountry": "RO"
    }
};

// Game id → publisher brand, mirrors api card-feed mapping. Fallback for
// when the joined cardGame.name isn't on the storefront object.
const GAME_BRAND = { 1: "Magic: The Gathering", 3: "Yu-Gi-Oh! TCG", 6: "Pokémon TCG" };

const WebSite = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://krit.ro/#website",
    "url": "https://krit.ro",
    "name": "Krit.ro",
    "inLanguage": "ro-RO",
    "publisher": { "@id": "https://krit.ro/#organization" },
    "potentialAction": {
        "@type": "SearchAction",
        "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://krit.ro/cautare/{search_term_string}"
        },
        "query-input": "required name=search_term_string"
    }
})

const LocalBusiness = () => ({
    "@context": "https://schema.org",
    "@id": "https://krit.ro/#organization",
    "@type": "Store",
    "name": "Krit.ro - Boardgame & Pop Culture Store",
    "url": "https://krit.ro",
    "logo": "https://krit.ro/img/logo.png",
    "image": "https://krit.ro/img/og-logo.a.png",
    "email": "contact@krit.ro",
    "telephone": "+40741578578",
    "priceRange": "$$",
    "currenciesAccepted": "RON",
    "sameAs": SOCIAL_PROFILES,
    "address": { "@id": "https://krit.ro/#hqaddress", ...STORE_ADDRESSES.ploiesti },
    "location": { "@id": "https://krit.ro/#hqaddress" },
    "geo": {
        "@type": "GeoCoordinates",
        "latitude": "44.9355573",
        "longitude": "26.0366852"
    },
    "openingHoursSpecification": PLOIESTI_HOURS,
    // Second physical store as a department so the single #organization
    // entity (referenced as publisher/seller elsewhere) owns both stores.
    "department": [{
        "@type": "Store",
        "name": "Krit.ro - Magazin Brașov",
        "telephone": "+40741805805",
        "email": "contact@krit.ro",
        "address": STORE_ADDRESSES.brasov,
        "openingHoursSpecification": BRASOV_HOURS
    }]
})

const Product = (product) => {
    if (!product) return null;

    // Ensure images is an array, default to empty array if not
    const images = Array.isArray(product.images) ? product.images : [];

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.metaTitle || product.title,
        "image": images.map(image => `${process.env.imageStorage}/default_${image.image}.jpg`),
        "description": product.metaDescription,
        "sku": String(product.sku || product.id),
        "offers": {
            "@type": "Offer",
            "url": "https://krit.ro/produs/" + product.slug,
            "priceCurrency": "RON",
            "price": product.price,
            "priceValidUntil": dayjs().add(1, 'week').format('YYYY-MM-DD') ,
            "availability": availabilitySchema(product),
            "itemCondition": "https://schema.org/NewCondition",
            "seller": {
                "@id": "https://krit.ro/#organization"
            }
        }
    };

    // Identifiers / brand emitted only when present — never assert a value
    // we don't have (Google penalizes fabricated brand/GTIN).
    if (product.brand) data.brand = { "@type": "Brand", "name": product.brand };
    if (product.category && product.category.title) data.category = product.category.title;
    const gtin = String(product.barcode || '').replace(/\D/g, '');
    if (gtin.length >= 12) data.gtin13 = gtin.slice(-13);
    else if (gtin.length === 8) data.gtin8 = gtin;

    // Approved buyer reviews only (the api never sends pending ones).
    const reviews = product.reviews;
    if (reviews && reviews.count > 0) {
        data.aggregateRating = { "@type": "AggregateRating", "ratingValue": reviews.average, "reviewCount": reviews.count, "bestRating": 5, "worstRating": 1 };
        data.review = reviews.items.map(review => ({
            "@type": "Review",
            "reviewRating": { "@type": "Rating", "ratingValue": review.rating, "bestRating": 5, "worstRating": 1 },
            "author": { "@type": "Person", "name": review.authorName },
            "datePublished": dayjs(review.createdAt).format('YYYY-MM-DD'),
            ...(review.comment ? { "reviewBody": review.comment } : {})
        }));
    }

    return data;
}

const Card = (product) => {
    if (!product) return null;

    const gameName = (product.cardGame && product.cardGame.name) || GAME_BRAND[product.cardGameId] || "TCG";
    const setName = product.expansion && product.expansion.name;

    // Keyword-rich, unique-per-card description so the page isn't just a name.
    const descParts = [`${product.name} – carte ${gameName} individuală (single)`];
    if (setName) descParts.push(`din setul ${setName}`);
    if (product.rarity) descParts.push(`raritate ${product.rarity}`);

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.metaTitle || product.name,
        "image": cardImageUrl(product.image),
        "description": descParts.join(", ") + ".",
        "brand": { "@type": "Brand", "name": gameName },
        "category": "Trading Card Game Single",
        "offers": {
            "@type": "Offer",
            "url": "https://krit.ro" + singleCardUrl(product),
            "priceCurrency": "RON",
            "price": product.price,
            "priceValidUntil": dayjs().add(1, 'week').format('YYYY-MM-DD') ,
            "availability": availabilitySchema(product),
            "itemCondition": "https://schema.org/NewCondition",
            "seller": {
                "@id": "https://krit.ro/#organization"
            }
        }
    };

    if (product.code) data.sku = String(product.code);

    const additionalProperty = [];
    if (setName) additionalProperty.push({ "@type": "PropertyValue", "name": "Set", "value": setName });
    if (product.rarity) additionalProperty.push({ "@type": "PropertyValue", "name": "Raritate", "value": String(product.rarity) });
    if (product.code) additionalProperty.push({ "@type": "PropertyValue", "name": "Cod", "value": String(product.code) });
    if (additionalProperty.length) data.additionalProperty = additionalProperty;

    return data;
}

// Physical-event markup for organized play. Location resolves to the
// matching store address; offers are emitted only when a price is set.
const Event = (event) => {
    if (!event || !event.start) return null;
    const start = toIso(event.start);
    if (!start) return null;

    const rawLocation = typeof event.location === 'string'
        ? event.location
        : (event.location && event.location.name) || '';
    const lower = rawLocation.toLowerCase();
    const key = lower.includes('bra') ? 'brasov' : lower.includes('ploie') ? 'ploiesti' : null;
    const address = key
        ? STORE_ADDRESSES[key]
        : { "@type": "PostalAddress", "streetAddress": event.address || rawLocation || "Krit", "addressCountry": "RO" };

    const data = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": event.title,
        "startDate": start,
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "eventStatus": event.status === 'cancelled'
            ? "https://schema.org/EventCancelled"
            : "https://schema.org/EventScheduled",
        "url": `https://krit.ro/calendar/event/${event.slug}`,
        "image": [`https://content.krit.ro/public-assets/event-images/preview_${event.id}_r${event.revision || 1}.jpg`],
        "location": { "@type": "Place", "name": rawLocation || "Krit", "address": address },
        "organizer": { "@type": "Organization", "name": "Krit.ro", "url": "https://krit.ro" }
    };

    const end = toIso(event.end);
    if (end) data.endDate = end;
    const description = stripTags(event.description);
    if (description) data.description = description.substring(0, 500);
    if (event.price != null) {
        data.offers = {
            "@type": "Offer",
            "price": event.price,
            "priceCurrency": "RON",
            "availability": "https://schema.org/InStock",
            "url": `https://krit.ro/calendar/event/${event.slug}`,
            "validFrom": start
        };
    }

    return data;
}

const ItemList = (items, name) => {
    const valid = (Array.isArray(items) ? items : []).filter((it) => it && it.url && it.name);
    if (valid.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "ItemList",
        ...(name ? { name } : {}),
        "itemListElement": valid.map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
                "@type": "Product",
                "name": it.name,
                "url": it.url.startsWith('http') ? it.url : `https://krit.ro${it.url}`,
                ...(it.image ? { "image": it.image } : {}),
                ...(it.price != null ? {
                    "offers": { "@type": "Offer", "price": it.price, "priceCurrency": "RON", "availability": "https://schema.org/InStock" }
                } : {})
            }
        }))
    };
}

const Breadcrumbs = (breadcrumbs, endPath) => {
    if (!breadcrumbs || !Array.isArray(breadcrumbs)) return null;

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs
            .filter(breadcrumb => breadcrumb && breadcrumb.label)
            .map((breadcrumb, i) => {
                const item = {
                    "@type": "ListItem",
                    "position": i + 1,
                    "name": breadcrumb.label,
                };
                // Omit `item` on the leaf crumb — schema.org wants no URL on
                // the current page, and a null value trips validators.
                if (breadcrumb.as) item.item = 'https://krit.ro' + breadcrumb.as;
                return item;
            })
    };
}


const StructuredData = (props) => {
    let structuredDataContent = props.type === 'LocalBusiness' ? LocalBusiness() :
                                props.type === 'WebSite' ? WebSite() :
                                props.type === 'Product' ? Product(props.product) :
                                props.type === 'Card' ? Card(props.product) :
                                props.type === 'Event' ? Event(props.event) :
                                props.type === 'ItemList' ? ItemList(props.items, props.name) :
                                props.type === 'Breadcrumbs' ? Breadcrumbs(props.breadcrumbs, props.endPath) : null

    // Don't render if no valid structured data
    if (!structuredDataContent) {
        return null;
    }

    return (<Head>
        <script type="application/ld+json" dangerouslySetInnerHTML={ { __html: JSON.stringify(structuredDataContent)}} />
    </Head>)
}

export default StructuredData;
